import {
  Component,
  OnInit,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { finalize } from 'rxjs';

import {
  DecimalOnlyDirective,
  DigitsOnlyDirective,
} from '../../core/directives';

import { InventoryItem } from '../../core/models/inventory.models';
import { ExportService } from '../../core/services/export.service';
import { InventoryService } from '../../core/services/inventory.service';
import { SettingsService } from '../../core/services/settings.service';
import { AuthService } from '../auth/auth.service';
import { confirmAction } from '../../core/utils/confirm.util';
import {
  defaultBranchFilterId,
  isBranchFilterLocked,
  matchesBranchFilter,
  resetBranchFilterId,
} from '../../core/utils/branch-scope.util';
import { currentTimestamp, formatCurrency } from '../../core/utils/format.util';
import { environment } from '../../environments/environment';
import {
  parseNonNegativeNumber,
  requiredText,
} from '../../core/utils/stock-qty.util';

@Component({
  selector: 'app-item-master',
  imports: [FormsModule, RouterLink, DigitsOnlyDirective, DecimalOnlyDirective],
  standalone: true,
  templateUrl: './item-master.component.html',
  styleUrl: './item-master.component.css',
})
export class ItemMasterComponent implements OnInit {
  private readonly inventory = inject(InventoryService);
  private readonly settings = inject(SettingsService);
  private readonly exporter = inject(ExportService);
  protected readonly auth = inject(AuthService);
  private readonly route = inject(ActivatedRoute);

  readonly timestamp = currentTimestamp;
  readonly formatCurrency = formatCurrency;
  readonly categories = this.settings.activeCategories;
  readonly units = this.settings.activeUnits;
  readonly branches = this.settings.scopedBranches;
  readonly locations = this.settings.scopedLocations;
  readonly branchFilterLocked = computed(() => isBranchFilterLocked(this.auth));
  readonly allItems = this.inventory.allItems;

  // ============================================
  // STATE
  // ============================================
  readonly search = signal('');
  readonly categoryFilter = signal('all');
  readonly branchFilter = signal('all');
  readonly locationFilter = signal('all');
  readonly statusFilter = signal('all');
  readonly rentableFilter = signal('all');
  readonly page = signal(1);
  readonly pageSize = 10;

  readonly showItemModal = signal(false);
  readonly showToast = signal(false);
  readonly toastMessage = signal('');
  readonly toastType = signal<'success' | 'error' | 'info'>('success');
  readonly editingId = signal<string | null>(null);
  readonly deletingId = signal<string | null>(null);
  /** One serial per physical unit when adding several items under one name. */
  readonly serialRows = signal<string[]>(['']);

  readonly isEditing = computed(() => this.editingId() !== null);

  readonly newItem = signal({
    code: '',
    name: '',
    category: '',
    unit: '',
    branch: '',
    location: '',
    stockLevel: 0,
    reorderLevel: 0,
    price: 0,
    status: 'active' as InventoryItem['status'],
    icon: 'bi-box-seam',
    isRentable: false,
    serialNumbers: '',
    sameNameUnits: false,
  });

  constructor() {
    effect(() => {
      const branches = this.branches();
      if (!branches.length) {
        return;
      }
      this.branchFilter.set(defaultBranchFilterId(this.auth, branches));
    });
  }

  readonly stats = computed(() => {
    const items = this.allItems();
    const totalItems = items.length;
    const activeItems = items.filter((i) => i.status === 'active').length;
    const lowStock = items.filter(
      (i) => i.stockLevel <= i.reorderLevel && i.status === 'active',
    ).length;
    const discontinued = items.filter(
      (i) => i.status === 'discontinued',
    ).length;
    const rentableItems = items.filter((i) => i.isRentable === true).length;
    return { totalItems, activeItems, lowStock, discontinued, rentableItems };
  });

  private matchesNonCategoryFilters(item: InventoryItem): boolean {
    const search = this.search().trim().toLowerCase();
    const branch = this.branchFilter();
    const location = this.locationFilter();
    const status = this.statusFilter();
    const rentable = this.rentableFilter();

    let matchesSearch = true;
    if (search) {
      const branchText = (item.branch ?? '').toLowerCase();
      const locationText = (item.location ?? '').toLowerCase();
      matchesSearch =
        item.code.toLowerCase().includes(search) ||
        item.name.toLowerCase().includes(search) ||
        item.category.toLowerCase().includes(search) ||
        branchText.includes(search) ||
        locationText.includes(search);
    }

    const matchesBranch =
      branch === 'all'
        ? true
        : matchesBranchFilter(
            branch,
            item.branch,
            item.branchId,
            this.branches(),
          );

    const locationName =
      this.locations().find((row) => row.id === location)?.name ?? '';
    const matchesLocation =
      location === 'all' ||
      item.location === location ||
      item.locationId === location ||
      (locationName !== '' && item.location === locationName);

    const matchesStatus = status === 'all' || item.status === status;

    let matchesRentable = true;
    if (rentable === 'true') {
      matchesRentable = item.isRentable === true;
    } else if (rentable === 'false') {
      matchesRentable = item.isRentable === false;
    }

    return (
      matchesSearch &&
      matchesBranch &&
      matchesLocation &&
      matchesStatus &&
      matchesRentable
    );
  }

  private matchesCategory(item: InventoryItem, category: string): boolean {
    if (category === 'all') {
      return true;
    }
    const categoryName =
      this.categories().find((row) => row.id === category)?.name ?? '';
    if (item.category === category || item.categoryId === category) {
      return true;
    }
    return categoryName !== '' && item.category === categoryName;
  }

  readonly filteredItems = computed(() => {
    const category = this.categoryFilter();
    return this.allItems().filter(
      (item) =>
        this.matchesNonCategoryFilters(item) &&
        this.matchesCategory(item, category),
    );
  });

  /** Products + total on-hand for the current filtered list (incl. selected category). */
  readonly filteredStockSummary = computed(() => {
    const rows = this.filteredItems();
    return {
      productCount: rows.length,
      totalUnits: rows.reduce((sum, item) => sum + (item.stockLevel || 0), 0),
    };
  });

  /** Stock totals by category (respects branch/location/status/search, not category filter). */
  readonly categoryStockCounts = computed(() => {
    const byCategory = new Map<
      string,
      { id: string; name: string; products: number; units: number }
    >();

    for (const item of this.allItems()) {
      if (!this.matchesNonCategoryFilters(item)) {
        continue;
      }
      const name = (item.category || 'Uncategorised').trim() || 'Uncategorised';
      const id =
        item.categoryId ||
        this.categories().find((row) => row.name === name)?.id ||
        name;
      const prev = byCategory.get(id) ?? {
        id,
        name,
        products: 0,
        units: 0,
      };
      prev.products += 1;
      prev.units += item.stockLevel || 0;
      byCategory.set(id, prev);
    }

    return [...byCategory.values()].sort(
      (a, b) => b.units - a.units || a.name.localeCompare(b.name),
    );
  });

  readonly selectedCategoryStock = computed(() => {
    const category = this.categoryFilter();
    if (category === 'all') {
      return null;
    }
    const match =
      this.categoryStockCounts().find((row) => row.id === category) ??
      this.categoryStockCounts().find(
        (row) =>
          row.name ===
          (this.categories().find((c) => c.id === category)?.name || category),
      );
    if (match) {
      return match;
    }
    const summary = this.filteredStockSummary();
    const name =
      this.categories().find((c) => c.id === category)?.name || category;
    return {
      id: category,
      name,
      products: summary.productCount,
      units: summary.totalUnits,
    };
  });

  readonly paginatedItems = computed(() => {
    const start = (this.page() - 1) * this.pageSize;
    return this.filteredItems().slice(start, start + this.pageSize);
  });

  readonly totalPages = computed(() =>
    Math.max(1, Math.ceil(this.filteredItems().length / this.pageSize)),
  );

  readonly paginationStart = computed(() =>
    this.filteredItems().length === 0
      ? 0
      : (this.page() - 1) * this.pageSize + 1,
  );

  readonly paginationEnd = computed(() =>
    Math.min(this.page() * this.pageSize, this.filteredItems().length),
  );

  readonly pageNumbers = computed(() =>
    Array.from({ length: this.totalPages() }, (_, i) => i + 1),
  );

  readonly modalLocations = computed(() => {
    const branchId = this.newItem().branch;
    if (!branchId) {
      return this.locations();
    }
    return this.locations().filter(
      (loc) => !loc.branchId || loc.branchId === branchId,
    );
  });

  ngOnInit(): void {
    const rentable = this.route.snapshot.queryParamMap.get('rentable');
    if (rentable === 'true' || rentable === 'false') {
      this.rentableFilter.set(rentable);
    }

    if (!environment.useMockData) {
      this.settings.refreshFromApi();
      this.inventory.loadItemMasterAndStock().subscribe();
    }
  }

  stockLevelClass(item: any): string {
    if (item.stockLevel === 0) return 'out';
    if (item.stockLevel <= item.reorderLevel) return 'low';
    return 'ok';
  }

  statusLabel(status: InventoryItem['status']): string {
    return status.charAt(0).toUpperCase() + status.slice(1);
  }

  /** Coerce select ngModel values for template bindings (no global String()). */
  asSelectValue(value: unknown): string {
    return value == null ? '' : String(value);
  }

  parseSerialNumbers(value: string): string[] {
    return value
      .split(/\r?\n|,/)
      .map((serial) => serial.trim())
      .filter((serial) => serial.length > 0);
  }

  setSameNameUnits(enabled: boolean): void {
    this.newItem.update((item) => ({
      ...item,
      sameNameUnits: enabled,
      serialNumbers: '',
      code: enabled ? '' : item.code,
    }));
    const stock = Number(this.newItem().stockLevel) || 0;
    this.serialRows.set(enabled && stock > 0 ? [''] : []);
  }

  addSerialRow(): void {
    const max = Number(this.newItem().stockLevel) || 0;
    if (this.serialRows().length >= max) {
      this.showToastMessage(
        max > 0
          ? `You can enter at most ${max} serial number${max === 1 ? '' : 's'}, one for each item.`
          : 'Enter the number of items before adding serial numbers.',
        'error',
      );
      return;
    }
    this.serialRows.update((rows) => [...rows, '']);
  }

  removeSerialRow(index: number): void {
    this.serialRows.update((rows) =>
      rows.length <= 1 ? rows : rows.filter((_, rowIndex) => rowIndex !== index),
    );
  }

  updateSerialRow(index: number, value: string): void {
    this.serialRows.update((rows) =>
      rows.map((serial, rowIndex) => (rowIndex === index ? value : serial)),
    );
  }

  filledSerialCount(): number {
    return this.serialRows().filter((serial) => serial.trim().length > 0).length;
  }

  updateNewItem(
    field: keyof ReturnType<typeof this.newItem>,
    value: string | number | boolean,
  ): void {
    if (field === 'stockLevel' || field === 'reorderLevel') {
      const parsed = parseNonNegativeNumber(
        value,
        field === 'stockLevel' ? 'Stock level' : 'Reorder level',
      );
      if (!parsed.ok) {
        this.showToastMessage(
          parsed.message ?? 'Enter a valid number',
          'error',
        );
        return;
      }
      value = parsed.value;
      if (field === 'stockLevel' && this.newItem().sameNameUnits) {
        const nextQty = parsed.value;
        const filled = this.serialRows().filter((serial) => serial.trim()).length;
        if (filled > nextQty) {
          this.showToastMessage(
            'Serial numbers cannot be more than the number of items. Remove a serial first.',
            'error',
          );
          return;
        }
        this.serialRows.update((rows) => {
          const kept = [...rows];
          while (kept.length > nextQty && !kept[kept.length - 1]?.trim()) {
            kept.pop();
          }
          return kept.slice(0, nextQty);
        });
      }
    }
    if (field === 'price') {
      const parsed = parseNonNegativeNumber(value, 'Price');
      if (!parsed.ok) {
        this.showToastMessage(parsed.message ?? 'Enter a valid price', 'error');
        return;
      }
      value = parsed.value;
    }

    this.newItem.update((item) => {
      const next = { ...item, [field]: value };
      if (field === 'branch') {
        const stillValid = this.locations().some(
          (loc) =>
            loc.id === next.location &&
            (!loc.branchId || loc.branchId === String(value)),
        );
        if (!stillValid) {
          next.location = '';
        }
      }
      return next;
    });
  }

  onSearchChange(): void {
    this.page.set(1);
  }

  onFilterChange(): void {
    this.page.set(1);
  }

  selectCategoryStock(categoryId: string): void {
    this.categoryFilter.set(categoryId);
    this.onFilterChange();
  }

  clearFilters(): void {
    this.search.set('');
    this.categoryFilter.set('all');
    this.branchFilter.set(resetBranchFilterId(this.auth, this.branches()));
    this.locationFilter.set('all');
    this.statusFilter.set('all');
    this.rentableFilter.set('all');
    this.page.set(1);
  }

  private showToastMessage(
    message: string,
    type: 'success' | 'error' | 'info' = 'success',
  ): void {
    this.toastMessage.set(message);
    this.toastType.set(type);
    this.showToast.set(true);
    setTimeout(() => this.showToast.set(false), 4000);
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages()) {
      this.page.set(page);
    }
  }

  openItemModal(): void {
    this.editingId.set(null);
    this.newItem.set({
      code: '',
      name: '',
      category: '',
      unit: '',
      branch: '',
      location: '',
      stockLevel: 0,
      reorderLevel: 0,
      price: 0,
      status: 'active',
      icon: 'bi-box-seam',
      isRentable: false,
      serialNumbers: '',
      sameNameUnits: false,
    });
    this.serialRows.set(['']);
    this.showItemModal.set(true);
    document.body.style.overflow = 'hidden';
  }

  openEditModal(item: InventoryItem): void {
    const populate = (source: InventoryItem) => {
      this.editingId.set(source.id);
      this.newItem.set({
        code: source.code,
        name: source.name,
        category: String(this.resolveCategoryId(source)),
        unit: String(this.resolveUnitId(source)),
        branch: String(this.resolveBranchId(source)),
        location: String(this.resolveLocationId(source)),
        stockLevel: source.stockLevel,
        reorderLevel: source.reorderLevel,
        price: source.price,
        status: source.status,
        icon: source.icon ?? 'bi-box-seam',
        isRentable: source.isRentable ?? false,
        serialNumbers: '',
        sameNameUnits: false,
      });
      this.showItemModal.set(true);
      document.body.style.overflow = 'hidden';
    };

    if (!environment.useMockData) {
      this.settings.refreshFromApi();
    }

    // Reload item master when IDs are missing so Category/UOM can bind.
    const needsReload =
      !environment.useMockData &&
      (!String(item.categoryId ?? '').trim() ||
        !String(item.unitId ?? '').trim());

    if (needsReload) {
      this.inventory.loadItemMasterAndStock().subscribe({
        next: () => {
          const fresh =
            this.allItems().find((row) => row.id === item.id) ?? item;
          populate(fresh);
        },
        error: () => populate(item),
      });
      return;
    }

    populate(item);
  }

  private resolveCategoryId(item: InventoryItem): string {
    const byId = String(item.categoryId ?? '').trim();
    // Prefer the saved ID even if lookup lists have not loaded yet.
    if (byId) {
      return byId;
    }

    const label = String(item.category ?? '').trim().toLowerCase();
    if (!label) {
      return '';
    }

    return (
      this.categories().find(
        (row) => row.name.trim().toLowerCase() === label,
      )?.id ??
      this.categories().find(
        (row) => row.code.trim().toLowerCase() === label,
      )?.id ??
      ''
    );
  }

  private resolveUnitId(item: InventoryItem): string {
    const byId = String(item.unitId ?? '').trim();
    if (byId) {
      return byId;
    }

    const label = String(item.unit ?? '').trim().toLowerCase();
    if (!label || label === 'unit') {
      return '';
    }

    return (
      this.units().find((row) => row.name.trim().toLowerCase() === label)?.id ??
      this.units().find((row) => row.code.trim().toLowerCase() === label)?.id ??
      this.units().find(
        (row) => row.abbreviation.trim().toLowerCase() === label,
      )?.id ??
      ''
    );
  }

  private resolveBranchId(item: InventoryItem): string {
    const byId = String(item.branchId ?? '').trim();
    if (byId) {
      return byId;
    }

    const label = String(item.branch ?? '').trim().toLowerCase();
    if (!label) {
      return '';
    }

    return (
      this.branches().find((row) => row.name.trim().toLowerCase() === label)
        ?.id ?? ''
    );
  }

  private resolveLocationId(item: InventoryItem): string {
    const byId = String(item.locationId ?? '').trim();
    if (byId) {
      return byId;
    }

    const label = String(item.location ?? '').trim().toLowerCase();
    if (!label) {
      return '';
    }

    const branchId = this.resolveBranchId(item);
    return (
      this.locations().find(
        (row) =>
          row.name.trim().toLowerCase() === label &&
          (!branchId || row.branchId === branchId),
      )?.id ??
      this.locations().find((row) => row.name.trim().toLowerCase() === label)
        ?.id ??
      ''
    );
  }

  /** Options for edit modal — always include the item's current category/unit. */
  protected readonly categoryOptions = computed(() => {
    const list = [...this.categories()];
    const selectedId = String(this.newItem().category ?? '').trim();
    const selectedName = this.editingId()
      ? this.allItems().find((i) => i.id === this.editingId())?.category
      : '';
    if (
      selectedId &&
      !list.some((row) => String(row.id) === selectedId)
    ) {
      list.unshift({
        id: selectedId,
        code: '',
        name: selectedName?.trim() || `Category #${selectedId}`,
        description: '',
        status: 'active',
      });
    }
    return list.map((row) => ({ ...row, id: String(row.id) }));
  });

  protected readonly unitOptions = computed(() => {
    const list = [...this.units()];
    const selectedId = String(this.newItem().unit ?? '').trim();
    const selectedName = this.editingId()
      ? this.allItems().find((i) => i.id === this.editingId())?.unit
      : '';
    if (selectedId && !list.some((row) => String(row.id) === selectedId)) {
      list.unshift({
        id: selectedId,
        code: selectedName?.trim() || selectedId,
        name:
          selectedName?.trim() && selectedName.trim().toLowerCase() !== 'unit'
            ? selectedName.trim()
            : `Unit #${selectedId}`,
        abbreviation: '',
        status: 'active',
      });
    }
    return list.map((row) => ({ ...row, id: String(row.id) }));
  });

  closeItemModal(): void {
    this.showItemModal.set(false);
    this.editingId.set(null);
    document.body.style.overflow = 'auto';
  }

  closeModalOnBackdrop(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('modal-overlay')) {
      this.closeItemModal();
    }
  }

  submitItem(): void {
    const item = this.newItem();
    const isEditing = !!this.editingId();

    const nameCheck = requiredText(item.name, 'Name');
    const categoryCheck = requiredText(item.category, 'Category');
    const unitCheck = requiredText(item.unit, 'Unit');
    const branchCheck = requiredText(item.branch, 'Branch');
    const locationCheck = requiredText(item.location, 'Location');
    if (
      !nameCheck.ok ||
      !categoryCheck.ok ||
      !unitCheck.ok ||
      !branchCheck.ok ||
      !locationCheck.ok
    ) {
      this.showToastMessage(
        nameCheck.message ??
          categoryCheck.message ??
          unitCheck.message ??
          branchCheck.message ??
          locationCheck.message ??
          'Please fill in all required fields',
        'error',
      );
      return;
    }

    const stockParsed = parseNonNegativeNumber(item.stockLevel, 'Stock level');
    const reorderParsed = parseNonNegativeNumber(
      item.reorderLevel,
      'Reorder level',
    );
    const priceParsed = parseNonNegativeNumber(item.price, 'Price');
    if (!stockParsed.ok) {
      this.showToastMessage(
        stockParsed.message ?? 'Stock level cannot be negative',
        'error',
      );
      return;
    }
    if (!reorderParsed.ok) {
      this.showToastMessage(
        reorderParsed.message ?? 'Reorder level cannot be negative',
        'error',
      );
      return;
    }
    if (!priceParsed.ok) {
      this.showToastMessage(
        priceParsed.message ?? 'Price cannot be negative',
        'error',
      );
      return;
    }

    const useSerialUnits = !isEditing && item.sameNameUnits;
    const serials = useSerialUnits
      ? this.serialRows().map((serial) => serial.trim()).filter((serial) => serial.length > 0)
      : [];
    if (useSerialUnits && serials.length > stockParsed.value) {
      this.showToastMessage(
        'Serial numbers cannot be more than the number of items.',
        'error',
      );
      return;
    }
    if (useSerialUnits && this.serialRows().some((serial) => !serial.trim())) {
      this.showToastMessage('Every serial row needs a serial number, or remove the empty row.', 'error');
      return;
    }
    const duplicateSerial = serials.find(
      (serial, index) =>
        serials.findIndex(
          (other) => other.toLowerCase() === serial.toLowerCase(),
        ) !== index,
    );
    if (duplicateSerial) {
      this.showToastMessage(
        `Serial number ${duplicateSerial} is listed more than once.`,
        'error',
      );
      return;
    }
    const itemCode = useSerialUnits ? '' : (item.code ?? '').trim();
    if (isEditing && !itemCode) {
      this.showToastMessage('Item code is required', 'error');
      return;
    }

    const category = this.categories().find((row) => row.id === item.category);
    const unit = this.units().find((row) => row.id === item.unit);
    const branch = this.branches().find((row) => row.id === item.branch);
    const location = this.locations().find((row) => row.id === item.location);

    const payload = {
      // Blank on create → API/SQL auto-generates ITM###.
      code: itemCode,
      name: item.name,
      category: category?.name ?? item.category,
      categoryId: item.category,
      unit: unit?.name ?? item.unit,
      unitId: item.unit,
      branch: branch?.name ?? item.branch,
      branchId: item.branch,
      location: location?.name ?? item.location,
      locationId: item.location,
      stockLevel: stockParsed.value,
      reorderLevel: reorderParsed.value,
      price: priceParsed.value,
      status: item.status,
      icon: item.icon,
      isRentable: item.isRentable,
      serialNumbers: serials.join('\n'),
    };

    if (isEditing) {
      this.inventory
        .updateItemViaApi(this.editingId()!, payload)
        .subscribe((result) => {
          if (result.success) {
            this.showToastMessage('Item updated successfully', 'success');
            this.closeItemModal();
          } else {
            this.showToastMessage(
              result.message ?? 'Failed to update item',
              'error',
            );
          }
        });
      return;
    }

    this.inventory.createItemViaApi(payload).subscribe((result) => {
      if (result.success) {
        this.showToastMessage(
          result.message || 'Item added successfully',
          'success',
        );
        this.closeItemModal();
      } else {
        this.showToastMessage(result.message ?? 'Failed to add item', 'error');
      }
    });
  }

  deleteItem(item: InventoryItem): void {
    if (!this.auth.hasPermission('delete_inventory')) {
      this.showToastMessage('You do not have permission to delete items.', 'error');
      return;
    }
    if (item.status === 'inactive' || this.deletingId()) {
      return;
    }

    if (
      !confirmAction(
        `Remove "${item.name}" (${item.code}) from the active catalog? It will be marked inactive. History is kept.`,
      )
    ) {
      return;
    }

    this.deletingId.set(item.id);
    this.inventory
      .deleteItemViaApi(item)
      .pipe(finalize(() => this.deletingId.set(null)))
      .subscribe((result) => {
        if (result.success) {
          this.showToastMessage(
            'Item removed from the active catalog',
            'success',
          );
        } else {
          this.showToastMessage(
            result.message ?? 'Failed to delete item',
            'error',
          );
        }
      });
  }

  exportData(): void {
    const rows = this.filteredItems().map((item) => ({
      code: item.code,
      name: item.name,
      category: item.category,
      unit: item.unit,
      branch: item.branch || 'N/A',
      location: item.location || 'N/A',
      stockLevel: item.stockLevel,
      reorderLevel: item.reorderLevel,
      price: item.price,
      status: item.status,
      isRentable: item.isRentable ? 'Yes' : 'No',
    }));

    if (!rows.length) {
      this.showToastMessage('No items match the current filters.', 'info');
      return;
    }

    this.exporter.downloadCsv(
      `item-master-${new Date().toISOString().slice(0, 10)}`,
      rows,
    );
  }
}
